import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FaCopy, FaKey, FaTrash } from "react-icons/fa";
import { api, timeAgo, type ApiKeyRow, type OrganizationSettings, type ProjectSummary, type Role } from "../api.ts";
import { Toggle } from "../components/Toggle.tsx";
import { Select } from "../components/Select.tsx";

type Tab = "Notification" | "API Keys" | "Organization";

export function SettingsPage({
  orgName,
  currentRole,
  onOrgDeleted,
}: {
  orgName: string;
  currentRole: Role;
  onOrgDeleted: () => void;
}) {
  const canAdmin = currentRole === "owner" || currentRole === "admin";
  const canManageKeys = currentRole !== "viewer";
  const tabs: Tab[] = [
    ...(canManageKeys ? ["API Keys" as const] : []),
    ...(canAdmin ? ["Notification" as const] : []),
    "Organization",
  ];
  const [tab, setTab] = useState<Tab>(canAdmin ? "Notification" : canManageKeys ? "API Keys" : "Organization");

  return (
    <div className="space-y-6">
      <section>
        <h1 className="mb-1 text-2xl font-semibold text-slate-100">Settings</h1>
        <p className="text-sm text-slate-400">Manage notifications and organization-level preferences.</p>
      </section>

      <div className="settings-tabs">
        {tabs.map((t) => (
          <button key={t} type="button" className={`settings-tab${tab === t ? " active" : ""}`} onClick={() => setTab(t)}>
            {t}
          </button>
        ))}
      </div>

      {tab === "Notification"
        ? <NotificationTab />
        : tab === "API Keys"
          ? <ApiKeysTab />
          : <OrganizationTab orgName={orgName} currentRole={currentRole} onOrgDeleted={onOrgDeleted} />}
    </div>
  );
}

function ApiKeysTab() {
  const [keys, setKeys] = useState<ApiKeyRow[]>([]);
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [name, setName] = useState("");
  const [scope, setScope] = useState("organization");
  const [permission, setPermission] = useState<"read" | "write">("read");
  const [createdSecret, setCreatedSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState<number | null>(null);

  async function refresh() {
    try {
      const [nextKeys, nextProjects] = await Promise.all([api.listApiKeys(), api.listProjects()]);
      setKeys(nextKeys);
      setProjects(nextProjects);
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { void refresh(); }, []);

  async function generate(event: React.FormEvent) {
    event.preventDefault();
    setCreating(true);
    try {
      const projectId = scope === "organization" ? null : Number(scope);
      const created = await api.createApiKey({ name, permission, projectId });
      setCreatedSecret(created.secret);
      setName("");
      await refresh();
      toast.success("API key generated.");
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setCreating(false);
    }
  }
  async function revoke(key: ApiKeyRow) {
    if (!confirm(`Revoke "${key.name}"? Requests using this key will stop working immediately.`)) return;
    setRevoking(key.id);
    try {
      await api.revokeApiKey(key.id);
      setKeys((current) => current.filter((item) => item.id !== key.id));
      toast.success("API key revoked.");
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setRevoking(null);
    }
  }
  async function copySecret() {
    if (!createdSecret) return;
    await navigator.clipboard.writeText(createdSecret);
    toast.success("API key copied.");
  }

  return (
    <div className="api-keys-layout">
      <section className="settings-card api-key-list">
        <div className="api-key-heading">
          <div><h2>API keys</h2><p className="muted text-slate-400">Use bearer keys to automate Offline SDK workflows.</p></div>
        </div>
        {createdSecret && (
          <div className="api-key-secret">
            <div><b>Copy this key now</b><span>For security, it won’t be shown again.</span></div>
            <code>{createdSecret}</code>
            <button type="button" onClick={copySecret}><FaCopy/> Copy</button>
          </div>
        )}
        {loading ? <p className="text-sm text-slate-500">Loading…</p> : keys.length === 0 ? (
          <div className="api-key-empty"><FaKey/><b>No API keys yet</b><span>Generate a key to automate media uploads and offline database builds.</span></div>
        ) : (
          <div className="api-key-table-wrap"><table className="api-key-table">
            <thead><tr><th>Name</th><th>Scope</th><th>Access</th><th>Last used</th><th/></tr></thead>
            <tbody>{keys.map((key) => <tr key={key.id}>
              <td><b>{key.name}</b><code>{key.keyPrefix}••••••••</code></td>
              <td>{key.projectId === null ? "Organization" : key.projectName}</td>
              <td><span className={`api-access-pill ${key.permission}`}>{key.permission}</span></td>
              <td>{key.lastUsedAt ? timeAgo(key.lastUsedAt) : "Never"}</td>
              <td><button type="button" disabled={revoking === key.id} onClick={() => revoke(key)} aria-label={`Revoke ${key.name}`}><FaTrash/></button></td>
            </tr>)}</tbody>
          </table></div>
        )}
      </section>

      <aside className="settings-card api-key-create">
        <h2>Generate API key</h2>
        <p className="muted text-slate-400">The secret is displayed only once.</p>
        <form onSubmit={generate}>
          <label><span>Key name</span><input value={name} onChange={(event) => setName(event.target.value)} placeholder="CI deployment" required minLength={2}/></label>
          <div className="api-key-field"><span>Scope</span><Select value={scope} onChange={setScope} options={[
            { value: "organization", label: "Organization", description: "Access every project in this organization." },
            ...projects.map((project) => ({ value: String(project.id), label: project.name, description: "Access only this project." })),
          ]} triggerClassName="api-key-select-trigger" menuClassName="api-key-select-menu"/></div>
          <div className="api-key-field"><span>Permission</span><Select value={permission} onChange={(value) => setPermission(value as "read" | "write")} options={[
            { value: "read", label: "Read", description: "Download generated offline databases." },
            { value: "write", label: "Write", description: "Upload media, rebuild, and download databases." },
          ]} triggerClassName="api-key-select-trigger" menuClassName="api-key-select-menu"/></div>
          <button type="submit" disabled={creating || name.trim().length < 2}>{creating ? "Generating…" : "Generate key"}</button>
        </form>
      </aside>
    </div>
  );
}

function NotificationTab() {
  const [settings, setSettings] = useState<OrganizationSettings | null>(null);
  const [savedSettings, setSavedSettings] = useState<OrganizationSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getSettings().then((value) => {
      setSettings(value);
      setSavedSettings(value);
    }).catch((e) => setError((e as Error).message));
  }, []);

  function patch(update: Partial<OrganizationSettings>) {
    if (!settings) return;
    setSettings({ ...settings, ...update });
    setError(null);
  }

  async function save() {
    if (!settings) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await api.updateSettings(settings);
      setSettings(saved);
      setSavedSettings(saved);
      toast.success("Notification preferences saved.");
    } catch (e) {
      const message = (e as Error).message;
      setError(message);
      toast.error(message || "Could not save notification preferences.");
    } finally {
      setSaving(false);
    }
  }

  if (!settings) return <p className="text-sm text-slate-500">{error ?? "Loading…"}</p>;
  const dirty = !!savedSettings && (
    settings.emailNotifications !== savedSettings.emailNotifications ||
    settings.capacityAlertNotifications !== savedSettings.capacityAlertNotifications
  );

  return (
    <div className="settings-card">
      <h2>Email notifications</h2>
      <p className="muted text-slate-400">Choose what this organization gets notified about by email.</p>

      <Toggle
        label="Email notifications"
        description="Send email updates to organization admins."
        checked={settings.emailNotifications}
        onChange={(v) => patch({ emailNotifications: v })}
      />

      <div className="settings-subrow">
        <Toggle
          label="Offline project storage capacity reached"
          description="Notify when an offline project's fingerprint database has reached its storage capacity."
          checked={settings.capacityAlertNotifications}
          disabled={!settings.emailNotifications}
          onChange={(v) => patch({ capacityAlertNotifications: v })}
        />
      </div>

      {error && <p className="mt-2 text-sm text-rose-400">{error}</p>}
      <div className="settings-save-row">
        <span>{dirty ? "You have unsaved changes." : "Preferences are up to date."}</span>
        <button type="button" className="settings-save-btn" disabled={!dirty || saving} onClick={save}>
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

function OrganizationTab({ orgName, currentRole, onOrgDeleted }: { orgName: string; currentRole: Role; onOrgDeleted: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canDelete = currentRole === "owner";

  async function onDelete() {
    setBusy(true);
    setError(null);
    try {
      await api.deleteOrganization();
      onOrgDeleted();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }

  return (
    <div className="settings-card danger-zone">
      <h2>Delete organization</h2>
      <p className="muted text-slate-400">
        Permanently delete <b className="text-slate-300">{orgName}</b> along with every project, media file, and fingerprint database it owns. This cannot be undone.
      </p>

      {!canDelete ? (
        <p className="text-sm text-slate-500">Only an owner can delete this organization.</p>
      ) : !confirming ? (
        <div className="danger-row">
          <span className="text-sm text-slate-500">This action is permanent.</span>
          <button type="button" className="danger-btn" onClick={() => setConfirming(true)}>
            Delete organization
          </button>
        </div>
      ) : (
        <div>
          <p className="text-sm text-slate-400">
            Type <b className="text-slate-200">{orgName}</b> to confirm.
          </p>
          <input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={orgName}
            className="confirm-input"
            autoFocus
          />
          {error && <p className="mb-2 text-sm text-rose-400">{error}</p>}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              className="rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-300 hover:bg-slate-800"
              onClick={() => { setConfirming(false); setConfirmText(""); setError(null); }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="danger-btn"
              disabled={busy || confirmText !== orgName}
              onClick={onDelete}
            >
              {busy ? "Deleting…" : "Permanently delete"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
